import React, { useState, useEffect, useRef } from 'react';
import { invoicesAPI, clientsAPI, casesAPI, timeEntriesAPI, expensesAPI, employeesAPI, outstandingBalancesAPI } from '../services/api';
import PageHeader from '../components/PageHeader';
import Modal from '../components/Modal';
import StatusBadge from '../components/StatusBadge';
import { Plus, Search, Download, Send, Trash2, Zap, ChevronDown, AlertCircle, Bell } from 'lucide-react';
import { toast } from 'react-toastify';

const EMPTY_FORM = { client: '', case: '', due_date: '', tax_rate: 15.5, notes: '', line_items: [{ description: '', quantity: 1, unit_price: 0 }] };

// ── More Actions dropdown for each invoice row ────────────────────────────────
function MoreActions({ inv, employees, onRefresh }) {
  const [open, setOpen] = useState(false);
  const [submitModal, setSubmitModal] = useState(false);
  const [approverId, setApproverId] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); onRefresh(); }
    catch (err) { toast.error(err.response?.data?.error || 'Action failed'); }
    setOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await invoicesAPI.submitForApproval(inv.id, approverId);
      toast.success('Submitted for approval'); setSubmitModal(false); onRefresh();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const handleDownloadPDF = async () => {
    try {
      const res = await invoicesAPI.pdf(inv.id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url;
      a.download = `INV-${inv.invoice_number}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
    setOpen(false);
  };

  return (
    <>
      <div className="flex items-center gap-1" ref={ref}>
        <button
          className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-blue-700 text-white rounded-md hover:bg-blue-800 transition-colors"
          onClick={() => act(() => invoicesAPI.send(inv.id), 'Invoice sent')}
          title="Send to client (auto-approves)"
        >
          <Send size={12} /> Send
        </button>
        <div className="relative">
          <button
            className="px-2 py-1.5 text-xs font-medium bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            onClick={() => setOpen(v => !v)}
          >
            More actions <ChevronDown size={11} className="inline ml-0.5" />
          </button>
          {open && (
            <div className="absolute right-0 top-full mt-1 w-52 bg-white rounded-lg shadow-lg border border-gray-200 z-30 py-1">
              {['Draft'].includes(inv.status) && (
                <button className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50"
                  onClick={() => { setOpen(false); setSubmitModal(true); }}>
                  Submit for approval
                </button>
              )}
              {['Draft', 'Submitted'].includes(inv.status) && (
                <button className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50"
                  onClick={() => act(() => invoicesAPI.approve(inv.id), 'Invoice approved')}>
                  Approve
                </button>
              )}
              {!['Paid', 'Archived', 'Cancelled'].includes(inv.status) && (
                <button className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50"
                  onClick={() => act(() => invoicesAPI.approveAndApplyTrust(inv.id), 'Paid from trust')}>
                  Approve &amp; apply trust
                </button>
              )}
              <button className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50"
                onClick={handleDownloadPDF}>
                Download PDF
              </button>
              {inv.status !== 'Archived' && (
                <button className="w-full text-left px-4 py-2.5 text-sm text-gray-500 hover:bg-gray-50"
                  onClick={() => act(() => invoicesAPI.archive(inv.id), 'Archived')}>
                  Archive
                </button>
              )}
              <div className="border-t border-gray-100 my-1" />
              <button className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50"
                onClick={() => { if (window.confirm('Delete this invoice?')) act(() => invoicesAPI.delete(inv.id), 'Deleted'); }}>
                Delete
              </button>
            </div>
          )}
        </div>
      </div>

      <Modal isOpen={submitModal} onClose={() => setSubmitModal(false)} title="Submit for Approval">
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-sm text-gray-600">
            Who should approve invoice <strong>INV-{inv.invoice_number}</strong>?
          </p>
          <div>
            <label className="label">Approver *</label>
            <select className="input" required value={approverId} onChange={e => setApproverId(e.target.value)}>
              <option value="">— Select approver —</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>{emp.full_name} — {emp.role}</option>
              ))}
            </select>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" className="btn-secondary" onClick={() => setSubmitModal(false)}>Cancel</button>
            <button type="submit" className="btn-primary">Submit</button>
          </div>
        </form>
      </Modal>
    </>
  );
}

// ── Outstanding Balances tab ──────────────────────────────────────────────────
function OutstandingBalancesTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    outstandingBalancesAPI.get()
      .then(r => setRows(r.data))
      .catch(() => toast.error('Could not load outstanding balances'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const toggleReminder = async (clientId, current) => {
    try {
      await clientsAPI.toggleBillReminders(clientId, !current);
      setRows(prev => prev.map(r => r.client_id === clientId ? { ...r, bill_reminders_enabled: !current } : r));
      toast.success(!current ? 'Reminders enabled' : 'Reminders disabled');
    } catch {
      toast.error('Failed to update reminder setting');
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-40">
      <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" />
    </div>
  );

  if (rows.length === 0) return (
    <div className="text-center py-16 text-gray-400">
      <AlertCircle size={36} className="mx-auto mb-3 text-gray-300" />
      <p className="font-medium">No outstanding balances</p>
      <p className="text-sm">All clients are up to date.</p>
    </div>
  );

  return (
    <div className="card p-0 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 border-b">
          <tr>
            {['Client', 'Invoices', 'Total Outstanding', 'Oldest Due', 'Last Sent', 'Auto Reminders'].map(h => (
              <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map(row => (
            <tr key={row.client_id} className="hover:bg-gray-50">
              <td className="px-4 py-3">
                <a href={`/clients/${row.client_id}`} className="font-medium text-blue-700 hover:underline">{row.client_name}</a>
                {row.email && <div className="text-xs text-gray-400">{row.email}</div>}
              </td>
              <td className="px-4 py-3 text-gray-600">{row.invoice_count}</td>
              <td className="px-4 py-3 font-semibold text-red-600">
                US${parseFloat(row.total_outstanding).toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </td>
              <td className="px-4 py-3 text-gray-500">{row.oldest_due_date || '—'}</td>
              <td className="px-4 py-3 text-gray-500 text-xs">
                {row.last_sent ? new Date(row.last_sent).toLocaleDateString() : 'Never'}
              </td>
              <td className="px-4 py-3">
                <button
                  onClick={() => toggleReminder(row.client_id, row.bill_reminders_enabled)}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                    row.bill_reminders_enabled ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                  title={row.bill_reminders_enabled ? 'Disable reminders' : 'Enable reminders'}
                >
                  <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                    row.bill_reminders_enabled ? 'translate-x-5' : 'translate-x-0.5'
                  }`} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Invoices() {
  const [activeTab,      setActiveTab]      = useState('invoices');
  const [invoices,       setInvoices]       = useState([]);
  const [clients,        setClients]        = useState([]);
  const [cases,          setCases]          = useState([]);
  const [employees,      setEmployees]      = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [statusFilter,   setStatusFilter]   = useState('');
  const [modalOpen,      setModalOpen]      = useState(false);
  const [form,           setForm]           = useState(EMPTY_FORM);
  const [saving,         setSaving]         = useState(false);
  // Unbilled items for the selected case
  const [caseEntries,    setCaseEntries]    = useState([]);  // time entries
  const [caseExpenses,   setCaseExpenses]   = useState([]);  // expenses
  const [selectedItems,  setSelectedItems]  = useState([]);  // array of {type, id, item}

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const { data } = await invoicesAPI.list(params);
      setInvoices(data.results || data);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    clientsAPI.list().then(r => setClients(r.data.results || r.data));
    casesAPI.list().then(r => setCases(r.data.results || r.data));
    employeesAPI.list({ status: 'Active' }).then(r => setEmployees(r.data.results || r.data));
  }, []);
  useEffect(() => { load(); }, [statusFilter]);

  // When a case is selected in the form, load its unbilled time entries and expenses
  useEffect(() => {
    if (!form.case) { setCaseEntries([]); setCaseExpenses([]); setSelectedItems([]); return; }
    Promise.all([
      timeEntriesAPI.list({ case: form.case, billing_status: 'Unbilled', billable: true }),
      expensesAPI.list({ case: form.case, billed_to_client: false }),
    ]).then(([tRes, eRes]) => {
      setCaseEntries(tRes.data.results || tRes.data);
      setCaseExpenses(eRes.data.results || eRes.data);
    }).catch(() => {});
  }, [form.case]);

  const toggleItem = (type, id) => {
    setSelectedItems(prev => {
      const key = `${type}-${id}`;
      const exists = prev.find(x => `${x.type}-${x.id}` === key);
      return exists ? prev.filter(x => `${x.type}-${x.id}` !== key) : [...prev, { type, id }];
    });
  };

  const isSelected = (type, id) => !!selectedItems.find(x => x.type === type && x.id === id);

  // Add selected unbilled items as line items
  const addSelectedAsLineItems = () => {
    const newItems = [];
    for (const { type, id } of selectedItems) {
      if (type === 'time') {
        const e = caseEntries.find(x => x.id === id);
        if (e) newItems.push({
          description: `${e.description || 'Time entry'} (${e.date}, ${e.duration_hours}h)`,
          quantity: parseFloat(e.duration_hours),
          unit_price: parseFloat(e.hourly_rate || 0),
          _time_entry_id: e.id,
        });
      } else {
        const ex = caseExpenses.find(x => x.id === id);
        if (ex) newItems.push({
          description: `${ex.category}: ${ex.description}`,
          quantity: 1,
          unit_price: parseFloat(ex.amount),
          _expense_id: ex.id,
        });
      }
    }
    if (newItems.length > 0) {
      const base = form.line_items.filter(it => it.description.trim());
      setForm(f => ({ ...f, line_items: [...base, ...newItems] }));
      setSelectedItems([]);
    }
  };

  const addLineItem  = () => setForm({ ...form, line_items: [...form.line_items, { description: '', quantity: 1, unit_price: 0 }] });
  const updateLineItem = (i, field, val) => {
    const items = [...form.line_items]; items[i] = { ...items[i], [field]: val };
    setForm({ ...form, line_items: items });
  };
  const removeLineItem = (i) => setForm({ ...form, line_items: form.line_items.filter((_, idx) => idx !== i) });

  const subtotal = form.line_items.reduce((s, it) => s + (parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0), 0);
  const tax   = subtotal * (parseFloat(form.tax_rate) || 0) / 100;
  const total = subtotal + tax;

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data: inv } = await invoicesAPI.create({
        client: form.client,
        case: form.case || null,
        due_date: form.due_date,
        tax_rate: form.tax_rate,
        total_amount: total.toFixed(2),
        notes: form.notes,
      });
      const validItems = form.line_items.filter(it => it.description && it.description.trim());
      for (const item of validItems) {
        await invoicesAPI.addLineItem({
          invoice: inv.id,
          description: item.description.trim(),
          quantity: parseFloat(item.quantity) || 1,
          unit_price: parseFloat(item.unit_price) || 0,
          time_entry: item._time_entry_id || null,
          expense: item._expense_id || null,
        });
      }
      toast.success('Invoice created');
      setModalOpen(false);
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      const detail = err.response?.data
        ? Object.entries(err.response.data).map(([k, v]) => `${k}: ${[].concat(v).join(', ')}`).join(' | ')
        : err.message;
      toast.error(`Failed: ${detail}`);
    } finally { setSaving(false); }
  };

  const statusSummary = {
    total:       invoices.reduce((s, i) => s + parseFloat(i.total_amount || 0), 0),
    paid:        invoices.filter(i => i.status === 'Paid').reduce((s, i) => s + parseFloat(i.paid_amount || 0), 0),
    outstanding: invoices.filter(i => ['Sent', 'Overdue', 'Partially Paid', 'Approved'].includes(i.status))
                         .reduce((s, i) => s + parseFloat(i.balance_due || 0), 0),
  };

  return (
    <div className="p-6">
      <PageHeader
        title="Billing"
        subtitle={activeTab === 'invoices' ? `${invoices.length} invoices` : 'Outstanding balances by client'}
        actions={
          activeTab === 'invoices' && (
            <button className="btn-primary flex items-center gap-2"
              onClick={() => { setForm(EMPTY_FORM); setCaseEntries([]); setCaseExpenses([]); setSelectedItems([]); setModalOpen(true); }}>
              <Plus size={16} /> New Invoice
            </button>
          )
        }
      />

      {/* Tab selector */}
      <div className="flex gap-1 border-b border-gray-200 mb-5">
        <button
          onClick={() => setActiveTab('invoices')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'invoices' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          All Invoices
        </button>
        <button
          onClick={() => setActiveTab('outstanding')}
          className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'outstanding' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Bell size={13} /> Outstanding Balances
        </button>
      </div>

      {activeTab === 'outstanding' && <OutstandingBalancesTab />}

      {activeTab === 'invoices' && (<>

      {/* KPI cards */}
      <div className="grid grid-cols-3 gap-4 mb-5">
        <div className="card text-center">
          <div className="text-xl font-bold text-gray-900">US${statusSummary.total.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
          <div className="text-xs text-gray-500 mt-1">Total Invoiced</div>
        </div>
        <div className="card text-center">
          <div className="text-xl font-bold text-green-700">US${statusSummary.paid.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
          <div className="text-xs text-gray-500 mt-1">Collected</div>
        </div>
        <div className="card text-center">
          <div className="text-xl font-bold text-amber-600">US${statusSummary.outstanding.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
          <div className="text-xs text-gray-500 mt-1">Outstanding</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-5">
        <select className="input w-52" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All Status</option>
          {['Draft', 'Submitted', 'Approved', 'Sent', 'Paid', 'Partially Paid', 'Overdue', 'Cancelled', 'Archived'].map(s => <option key={s}>{s}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>{['Invoice #', 'Client', 'Matter', 'Issue Date', 'Due Date', 'Total', 'Status', 'Actions'].map(h => (
                <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">{h}</th>
              ))}</tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading
                ? <tr><td colSpan={8} className="text-center py-12 text-gray-400">Loading...</td></tr>
                : invoices.length === 0
                ? <tr><td colSpan={8} className="text-center py-12 text-gray-400">No invoices yet.</td></tr>
                : invoices.map(inv => (
                  <tr key={inv.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-semibold text-blue-700">INV-{inv.invoice_number}</td>
                    <td className="px-4 py-3 text-gray-700">{inv.client_name}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs max-w-[160px] truncate">{inv.case_title || '—'}</td>
                    <td className="px-4 py-3 text-gray-500">{inv.issue_date}</td>
                    <td className="px-4 py-3 text-gray-500">{inv.due_date}</td>
                    <td className="px-4 py-3 font-semibold">US${parseFloat(inv.total_amount).toFixed(2)}</td>
                    <td className="px-4 py-3">
                      <StatusBadge status={inv.status === 'Submitted' ? 'Pending Approval' : inv.status} />
                      {inv.submitted_to_name && inv.status === 'Submitted' && (
                        <div className="text-xs text-gray-400 mt-0.5">→ {inv.submitted_to_name}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <MoreActions inv={inv} employees={employees} onRefresh={load} />
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Invoice Modal */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title="Create Invoice" size="lg">
        <form onSubmit={handleCreate} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Client *</label>
              <select className="input" required value={form.client} onChange={e => setForm({ ...form, client: e.target.value, case: '' })}>
                <option value="">Select client</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.client_name}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Case <span className="text-gray-400 font-normal text-xs">(loads unbilled items)</span></label>
              <select className="input" value={form.case} onChange={e => setForm({ ...form, case: e.target.value })}>
                <option value="">None</option>
                {cases
                  .filter(c => !form.client || String(c.client) === String(form.client))
                  .map(c => <option key={c.id} value={c.id}>{c.case_title}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Due Date *</label>
              <input type="date" className="input" required value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} />
            </div>
            <div>
              <label className="label">VAT Rate (%)</label>
              <input type="number" step="0.01" min="0" className="input" value={form.tax_rate} onChange={e => setForm({ ...form, tax_rate: e.target.value })} />
            </div>
          </div>

          {/* Unbilled time entries + expenses for selected case */}
          {form.case && (caseEntries.length > 0 || caseExpenses.length > 0) && (
            <div className="border border-blue-100 bg-blue-50 rounded-xl p-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-blue-700">
                  Unbilled items for this case — select to add as line items
                </p>
                {selectedItems.length > 0 && (
                  <button type="button" className="text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 px-2.5 py-1 rounded-md"
                    onClick={addSelectedAsLineItems}>
                    + Add {selectedItems.length} selected
                  </button>
                )}
              </div>
              <div className="max-h-44 overflow-y-auto space-y-1">
                {caseEntries.map(e => (
                  <label key={`t-${e.id}`} className={`flex items-start gap-2 p-2 rounded-lg cursor-pointer text-xs transition-colors ${isSelected('time', e.id) ? 'bg-blue-100' : 'hover:bg-blue-100/50'}`}>
                    <input type="checkbox" className="mt-0.5 rounded" checked={isSelected('time', e.id)} onChange={() => toggleItem('time', e.id)} />
                    <div className="flex-1 min-w-0">
                      <span className="font-medium text-gray-800">{e.description || 'Time entry'}</span>
                      <span className="text-gray-500 ml-2">{e.date} · {e.duration_hours}h @ US${e.hourly_rate}/h</span>
                    </div>
                    <span className="font-semibold text-gray-700 flex-shrink-0">US${parseFloat(e.amount || 0).toFixed(2)}</span>
                  </label>
                ))}
                {caseExpenses.map(ex => (
                  <label key={`e-${ex.id}`} className={`flex items-start gap-2 p-2 rounded-lg cursor-pointer text-xs transition-colors ${isSelected('expense', ex.id) ? 'bg-amber-50' : 'hover:bg-amber-50/50'}`}>
                    <input type="checkbox" className="mt-0.5 rounded" checked={isSelected('expense', ex.id)} onChange={() => toggleItem('expense', ex.id)} />
                    <div className="flex-1 min-w-0">
                      <span className="font-medium text-gray-800">{ex.category}</span>
                      <span className="text-gray-500 ml-2">{ex.description}</span>
                    </div>
                    <span className="font-semibold text-gray-700 flex-shrink-0">US${parseFloat(ex.amount).toFixed(2)}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Line Items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">Line Items</label>
              <button type="button" className="text-xs text-blue-600 hover:underline" onClick={addLineItem}>+ Add Item</button>
            </div>
            <div className="space-y-2">
              {form.line_items.map((item, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <div className="col-span-6">
                    <input className="input text-xs" placeholder="Description" value={item.description} onChange={e => updateLineItem(i, 'description', e.target.value)} />
                  </div>
                  <div className="col-span-2">
                    <input type="number" step="0.5" min="0" className="input text-xs" placeholder="Qty/Hrs" value={item.quantity} onChange={e => updateLineItem(i, 'quantity', e.target.value)} />
                  </div>
                  <div className="col-span-2">
                    <input type="number" step="0.01" min="0" className="input text-xs" placeholder="Rate (US$)" value={item.unit_price} onChange={e => updateLineItem(i, 'unit_price', e.target.value)} />
                  </div>
                  <div className="col-span-1 text-xs text-gray-600 text-right">
                    US${((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0)).toFixed(2)}
                  </div>
                  <div className="col-span-1">
                    <button type="button" className="text-gray-300 hover:text-red-500 p-1" onClick={() => removeLineItem(i)}>×</button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Totals */}
          <div className="bg-gray-50 rounded-lg p-4 text-sm space-y-1">
            <div className="flex justify-between"><span className="text-gray-600">Subtotal:</span><span>US${subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">VAT ({form.tax_rate}%):</span><span>US${tax.toFixed(2)}</span></div>
            <div className="flex justify-between font-bold text-base pt-1 border-t border-gray-200 mt-1">
              <span>Total:</span><span>US${total.toFixed(2)}</span>
            </div>
          </div>

          <div>
            <label className="label">Notes</label>
            <textarea className="input" rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" className="btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Creating…' : 'Create Invoice'}</button>
          </div>
        </form>
      </Modal>
      </>)}
    </div>
  );
}
